import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import NumberFormat from 'react-number-format';
import postal from 'postal';
import { setCaretPosition } from '../util';
import { selectInput } from '../util/inputUtils';

function areValuesSame(v1Param: number | null | undefined, v2Param: number | null | undefined) {
  let v1 = v1Param;
  let v2 = v2Param;

  if (v1Param === null || v1Param === undefined || isNaN(v1Param)) {
    v1 = null;
  }

  if (v2Param === null || v2Param === undefined || isNaN(v2Param)) {
    v2 = null;
  }

  return v1 === v2;
}

function setCaretPositionToEnd(elemId: string) {
  const elem = document.getElementById(elemId) as HTMLInputElement;
  const value = elem.value;
  const caretPos = value ? value.length : 0;

  setCaretPosition(elem, caretPos);

  setTimeout(() => {
    setCaretPosition(elem, caretPos);
  }, 0);
}

interface ExternalValueChangeRequest {
  external: boolean,
  value: number | null,
}

/**
 * @deprecated instead use Float with decimals = 0
 */
function Number({
  value,
  onChange = () => { },
  onEnter = undefined,
  disabled = false,
  min = undefined,
  max = undefined,
  floatId = undefined,
  onBlur = undefined,
  noborder = false,
  ...other
}: {
  value: number | null | undefined,
  onChange?: (x: number | null) => void,
  disabled?: boolean,
  onEnter?: (() => void) | undefined,
  min?: number | undefined,
  max?: number | undefined,
  floatId?: string | undefined,
  onBlur?: (() => Promise<boolean>) | undefined,
  noborder?: boolean,
  [x: string]: any
}) {
  const inputId = floatId || _.uniqueId('ifms-float-');
  const channel = postal.channel('ifms');
  const [//isTabPrevious
    , setIsTabPrevious] = useState(false);

  const [externalValueChangeRequest, setExternalValueChangeRequest] = useState<ExternalValueChangeRequest>({ external: false, value: null });

  let channelSubscription: ISubscriptionDefinition<any> | null = null;

  function subscribeChannels() {
    channelSubscription = postal.subscribe({
      channel: 'ifms',
      topic: 'ifms.set-number',
      callback: onValueChangeRequestByParent,
    });
  }

  function unsubscribeChannels() {
    if (channelSubscription) {
      postal.unsubscribe(channelSubscription);
    }
  }

  function isCell(element: HTMLInputElement): boolean {
    const divElements = document.querySelectorAll('div[id$="-editabletable"]');
    return Array.from(divElements).some(div => div.contains(element));
  }

  async function handleKeyDown(e: KeyboardEvent) {
    setIsTabPrevious(e.key === 'Tab' && e.shiftKey);

    if (e.key === 'Enter') {
      const target = e.target as HTMLInputElement
      if (onBlur && isCell(target)) {
        e.stopPropagation();
        e.preventDefault();
        const result = await onBlur();
        setTimeout(() => {
          if (result)
            channel.publish('ifms.editable-table-onEnter', { backFocus: false, changeFocus: true, element: target });
          else {
            selectInput(target)
          }
        }, 10);
      }
    }
  };

  function initializeEvent() {
    document.getElementById(inputId)?.addEventListener('keydown', handleKeyDown);
  }

  function unInitializeEvent() {
    document.getElementById(inputId)?.addEventListener('keydown', handleKeyDown);
  }

  useEffect(() => {
    if (inputId) {
      initializeEvent();
      return unInitializeEvent;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputId]);

  useEffect(() => {
    // Implementation note:
    //   The idea here is to not call handlers directly in postal callback, but to set state to avoid calling old version of react event handler (problems with references)
    if (!externalValueChangeRequest.external) {
      return;
    }

    doHandleChange(externalValueChangeRequest.value);
    setTimeout(() => setCaretPositionToEnd(inputId), 200);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalValueChangeRequest]);

  useEffect(() => {
    subscribeChannels();

    return () => {
      unsubscribeChannels();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onValueChangeRequestByParent({ parentId, number }: { parentId: string, number: number | null }) {
    const current = document.getElementById(inputId);

    if (current?.parentElement?.id === parentId) {
      setExternalValueChangeRequest({ external: true, value: number });
    }
  }

  // function onKeyDown(e:React.KeyboardEvent<HTMLInputElement>) {
  //   const up = 38;
  //   const down = 40;
  //   if (e.keyCode === up || e.keyCode === down) {
  //     // const parentId = (e?.target as Element|null)?.parentElement?.id;
  //     // if (!!parentId) {
  //     //   channel.publish('ifms.control-navigation', {
  //     //     parentId,
  //     //     keyCode: e.keyCode
  //     //   });
  //     // }
  //     // e.preventDefault();
  //     // e.stopPropagation();
  //   }
  // }

  async function handleBlur(e: React.FocusEvent) {
    const target = e.target as HTMLInputElement;
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (relatedTarget?.dataset?.nocellvalidate === 'true') {
      return;
    }
    if (relatedTarget?.classList.contains("ReactModal__Content")) {
      return;
    }

    if (onBlur) {
      const result = await onBlur();
      setTimeout(() => {
        if (result) {
          // if (!isTabPrevious)
          //   focusNextTabable(target);
          // else
          //   focusTabableByIndex(getCurrentTabableIndex(target) - 1);
        }
        else 
          selectInput(target)
      }, 10);
    }
  }

  function handleChange(n: any) {
    doHandleChange(n.floatValue);
  }

  function doHandleChange(floatValue: any) {
    if (min !== null && min !== undefined && floatValue !== null && floatValue !== undefined) {
      if (floatValue < min) {
        return;
      }
    }
    if (max !== null && max !== undefined && floatValue !== null && floatValue !== undefined) {
      if (floatValue > max) {
        return;
      }
    }

    if (!areValuesSame(value, floatValue)) {
      const newVal = floatValue !== undefined ? floatValue : null; // Small changed to be able to use change event with lodash merge (which ignores undefined values)
      onChange(newVal);
    }
  }

  function onClick(e: React.MouseEvent) {
    if (!disabled) {
      (e.target as HTMLInputElement).select();
    }
  }

  let cssClass = 'e-ifms-float e-ifms-input e-outline ifms-tabable';
  if (noborder) {
    cssClass = `${cssClass} ifms-noborder-input`;
  }
  if (!disabled) {
    cssClass = `${cssClass} ifms-tabable-active`;
  }


  const componentValue = value !== null && value !== undefined ? value : '';

  return (
    <NumberFormat
      id={inputId}
      thousandSeparator={true}
      allowLeadingZeros={false}
      className={cssClass}
      decimalScale={0}
      value={componentValue}
      onValueChange={handleChange}
      disabled={disabled}
      //onKeyDown={onKeyDown}
      onBlur={handleBlur}
      autoComplete="off"
      list="autocompleteOff"
      data-tooltip="-from-value"

      data-tooltip-onlywhencutoff
      onClick={onClick}
      isAllowed={values => {
        if (!values.value) {
          return true;
        }

        const minCheck = min === null || min === undefined || (values.floatValue ?? 0) >= min;
        const maxCheck = max === null || max === undefined || (values.floatValue ?? 0) <= max;

        return minCheck && maxCheck;
      }}
      {...other}
    />
  );
}

Number.defaultProps = {
  onChange: () => { },
  disabled: false,
  noborder: false,
};

Number.propTypes = {
  onChange: PropTypes.func.isRequired,
  onEnter: PropTypes.func,
  value: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  disabled: PropTypes.bool.isRequired,
  floatId: PropTypes.string,
  onBlur: PropTypes.func,
  noborder: PropTypes.bool.isRequired,
};


export default React.memo(Number);
